import axios from 'axios'
import { useMutation } from '@tanstack/react-query'

import type { TCheckoutSummary } from './useCheckoutSummary'
import { PrivateAPI } from '../utils/api'

type TSubmitCheckoutWarranty = {
  customerEmail: string
  customerName: string
  required: boolean
  type: 'WARRANTY_12' | 'WARRANTY_24'
}

type TSubmitCheckoutInput = {
  analysedFileId: string
  chosenPaymentMethodIndex: number
  checkoutSummary: Pick<TCheckoutSummary, 'paymentMethods'>
  ecuWarranty: boolean
  orderId?: string
  priority: boolean
  warranty: TSubmitCheckoutWarranty
}

type TSubmitCheckoutResponse = {
  status: 'PENDING'
} | {
  addonPaymentClientSecret?: string
  status: 'SUCCEEDED'
  upload: Record<string, unknown> & {
    id: string
  }
}

const getSubmitCheckoutErrorMessage = (err: unknown) => {
  const defaultMessage = 'An unexpected error occured'

  if (!axios.isAxiosError(err)) {
    return `${defaultMessage} [990]`
  }

  const status = err.response?.status

  if (!status) {
    return `${defaultMessage} [990]`
  }

  if (status >= 400 && status < 500) {
    return `${defaultMessage} [${status}]`
  }

  return defaultMessage
}

const useSubmitCheckoutUpload = () => {
  return useMutation<TSubmitCheckoutResponse, Error, TSubmitCheckoutInput>({
    mutationKey: ['submit-checkout-upload'],
    mutationFn: async ({
      analysedFileId,
      chosenPaymentMethodIndex,
      checkoutSummary,
      ecuWarranty,
      orderId,
      priority,
      warranty,
    }) => {
      try {
        const paymentMethodId =
          checkoutSummary.paymentMethods[chosenPaymentMethodIndex]?.id || ''

        const response = await PrivateAPI<TSubmitCheckoutResponse>({
          url: `/uploads/analyse/${analysedFileId}/checkout`,
          method: 'post',
          data: {
            priority,
            ecuWarranty,
            orderId,
            paymentMethodId,
            warranty,
          },
        })

        return response.data
      } catch (err) {
        throw new Error(getSubmitCheckoutErrorMessage(err))
      }
    },
  })
}

export default useSubmitCheckoutUpload
